import { useEffect } from 'react';

// Per-page <head> tags: title, description, canonical, Open Graph, Twitter
// card, robots and JSON-LD.
//
// Why a hook and not react-helmet-async: the project already manages the head
// by hand (RatesPage sets document.title), and public/index.html already
// carries one of every tag below. This EDITS those static tags in place rather
// than appending a second copy, so there is never a duplicate description or
// og:title in the DOM, and netlify/edge-functions/intake-preview.js, which
// finds and rewrites the same tags in the served shell, keeps working.
//
// What a crawler sees depends on who runs the bundle. Google does. Most other
// crawlers, every link scraper and every AI fetcher do not, which is why
// scripts/prerender.js runs the public pages at build time and writes out the
// head this hook produced. Tags this hook ADDS carry data-page-meta so
// utils/prerenderedHead.js can tell them from the shell's own.
//
// On unmount every tag goes back to the value the shell had, so navigating
// from /pricing to /login does not leave the pricing title behind.

// Absolute, and never window.location.origin: the pre-render runs on
// localhost, and that origin must not end up in a canonical URL.
export const SITE_URL = 'https://snowtrack.pro';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image-v2.png`;

const META_ATTR = 'data-page-meta';

// [selector, attribute that holds the value, attributes for a new element]
const tagSpec = (kind, key) => {
  if (kind === 'link') {
    return [`link[rel="${key}"]`, 'href', { rel: key }, 'link'];
  }
  const attr = kind === 'property' ? 'property' : 'name';
  return [`meta[${attr}="${key}"]`, 'content', { [attr]: key }, 'meta'];
};

function setTag(kind, key, value, restore) {
  const [selector, valueAttr, attrs, tagName] = tagSpec(kind, key);
  let el = document.head.querySelector(selector);
  if (el) {
    const previous = el.getAttribute(valueAttr);
    restore.push(() => el.setAttribute(valueAttr, previous));
  } else {
    el = document.createElement(tagName);
    Object.entries(attrs).forEach(([name, v]) => el.setAttribute(name, v));
    el.setAttribute(META_ATTR, '');
    document.head.appendChild(el);
    restore.push(() => el.remove());
  }
  el.setAttribute(valueAttr, value);
}

/**
 * @param {object} meta
 * @param {string} meta.title - document title, og:title and twitter:title
 * @param {string} [meta.description] - meta description, og and twitter
 * @param {string} [meta.path] - path for canonical and og:url, e.g. '/pricing'
 * @param {string} [meta.image] - absolute og:image URL
 * @param {string} [meta.robots] - e.g. 'noindex'
 * @param {object|object[]} [meta.jsonLd] - structured data for this page
 */
export default function usePageMeta({ title, description, path, image, robots, jsonLd }) {
  // Serialized so a caller can pass a fresh object every render without
  // re-running the effect.
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const restore = [];
    const previousTitle = document.title;

    if (title) {
      document.title = title;
      setTag('property', 'og:title', title, restore);
      setTag('name', 'twitter:title', title, restore);
    }
    if (description) {
      setTag('name', 'description', description, restore);
      setTag('property', 'og:description', description, restore);
      setTag('name', 'twitter:description', description, restore);
    }
    if (path) {
      const url = `${SITE_URL}${path}`;
      setTag('link', 'canonical', url, restore);
      setTag('property', 'og:url', url, restore);
    }
    const ogImage = image || DEFAULT_OG_IMAGE;
    setTag('property', 'og:image', ogImage, restore);
    setTag('name', 'twitter:image', ogImage, restore);
    setTag('name', 'twitter:card', 'summary_large_image', restore);
    if (robots) {
      setTag('name', 'robots', robots, restore);
    }

    let script = null;
    if (jsonLdText) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(META_ATTR, '');
      // JSON.stringify does not escape "<", so a value containing
      // "</script>" would end the element early.
      script.textContent = jsonLdText.replace(/</g, '\\u003c');
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      restore.reverse().forEach((undo) => undo());
      if (script) script.remove();
    };
  }, [title, description, path, image, robots, jsonLdText]);
}
